import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { Router } from '@angular/router';
import {
  deepCopy,
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormSectionStateService,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionReadonlyBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AdminManagerService } from '../../../admin/services/admin-manager.service';
import { AppMetaData, AppMetaDataItemNames, AssignmentEntity, AssignmentStudentEntity, StudentEntity } from '../../../../meta-data/app-meta-data.service';
import { StudentGroupSelectionValue } from '../../models/student-group-selection.model';
import { StudentRepositoryService } from '../../../students/services/student-repository.service';

@DwComponent({
  key: 'student-group-select-form-section',
  name: 'Student Group Select Form Section',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.Assignment,
  isGlobal: false,
})
@Component({
  selector: 'app-student-group-select-form-section',
  template: `
    <div class="dw-form-field">
      <div class="sg-form-label-row">
        <label ng-reflect-ng-class="label" class="label" for="assignment-student-group-select">
          Assign To
          <span title="Required" class="required-label">&nbsp;*</span>
          <i class="fa fa-info-circle ml-1" hidden title="undefined"></i>
        </label>
        <span class="sg-form-total" [title]="studentTotalTitle">{{ studentTotalLabel }}</span>
      </div>
      <ng-container *ngIf="showStudentGroupSelectControl; else readonly">
        <app-student-group-multi-select
          *ngIf="useStudentGroupMultiSelect; else legacyStudentGroupSelect"
          [(ngModel)]="selectionValue"
          [ngModelOptions]="{ standalone: true }"
          [flightSchoolId]="flightSchoolId"
          [courseId]="courseId"
          [showLabel]="false"
          [readOnly]="assignToLocked"
          [studentLabelOverrides]="studentLabelOverrides"
          inputId="assignment-student-group-select"
          (resolvedStudentIdsChange)="resolvedStudentIdsChanged($event)"
          (ignoredStudentCountChange)="ignoredStudentCountChanged($event)">
        </app-student-group-multi-select>
        <ng-template #legacyStudentGroupSelect>
          <app-student-group-select
            [(ngModel)]="selectionValue"
            [ngModelOptions]="{ standalone: true }"
            [flightSchoolId]="flightSchoolId"
            [courseId]="courseId"
            [showLabel]="false"
            [readOnly]="assignToLocked"
            [studentLabelOverrides]="studentLabelOverrides"
            inputId="assignment-student-group-select"
            (resolvedStudentIdsChange)="resolvedStudentIdsChanged($event)"
            (ignoredStudentCountChange)="ignoredStudentCountChanged($event)">
          </app-student-group-select>
        </ng-template>
      </ng-container>
      <div class="sg-form-validation" *ngIf="showAssignmentStudentValidationMessage">
        <dw-validation-info
          [formGroup]="assignmentStudentValidationControl"
          [submitted]="submitted"
          displayName="Assign To">
        </dw-validation-info>
      </div>
      <div class="sg-form-validation" *ngIf="showRootStudentValidationMessage">
        <dw-validation-info
          [formGroup]="formGroup"
          [submitted]="submitted"
          [filterKey]="studentCountValidationFilterKey"
          displayName="Assign To">
        </dw-validation-info>
      </div>
    </div>

    <!-- <pre>{{assignmentStudents?.value | json}}</pre> -->

    <ng-template #readonly>
      <div class="dw-chip-container" *ngIf="displayItems.length; else none">
        <div class="dw-chip" *ngFor="let item of displayItems; trackBy: trackDisplayItem">
          <span class="dw-chip-label">{{ item }}</span>
        </div>
      </div>
      <ng-template #none> 
        <div>None</div>
      </ng-template>
    </ng-template>
  `,
  styles: [`
    .sg-form-label-row {
      align-items: center;
      display: flex;
      justify-content: space-between;
      line-height: 1.2;
      margin-bottom: var(--card-eyebrow-gap);
    }

    .sg-form-label-row .label {
      margin-bottom: 0;
    }

    .sg-form-total {
      color: var(--text-tertiary);
      font-size: var(--font-size-xs);
      font-weight: var(--font-semibold);
    }

    .sg-form-validation {
      margin-top: 4px;
    }

    :host ::ng-deep .sg-form-validation .invalid-feedback {
      display: block;
    }
  `]
})
export class StudentGroupSelectFormSectionComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  assignmentStudents?: AbstractControl;
  assignmentStudentValidationControl?: any;
  formApi: DwMetaDataFormApi;
  selectionValue: StudentGroupSelectionValue = {
    studentIds: [],
    flightSchoolGroupIds: []
  };
  flightSchoolId: number;
  courseId: number;
  displayItems: string[] = [];
  studentTotalLabel = '0 students total';
  studentTotalTitle = '0 students will be assigned.';
  assignToLocked = false;
  showStudentGroupSelectControl = true;
  studentLabelOverrides: { [studentId: number]: string } = {};
  showAssignmentStudentValidationMessage = false;
  showRootStudentValidationMessage = false;
  readonly studentCountValidationFilterKey = 'assignment-student-count';
  readonly useStudentGroupMultiSelect = true;

  private readonly controlName = 'AssignmentStudent';
  private readonly destroy$ = new Subject<void>();
  private resolvedStudentIds: number[] = [];
  private routeLockedStudentDisplay: string;
  private routeLockedStudentId: number;
  private routeLockApplies = false;
  private routeLockInProgress = false;
  private sectionReadOnly = false;
  private selectionHydratedFromForm = false;
  private syncingFromForm = false;

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwFormSectionStateService: DwMetaDataFormSectionStateService,
    protected dwFormStateService: DwMetaDataFormStateService,
    private adminManagerService: AdminManagerService,
    private studentRepositoryService: StudentRepositoryService,
    private router: Router
  ) {
    super(dwExpressionService, dwFormSectionStateService, dwFormStateService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.sectionReadOnly = !!this.isSectionReadOnly();
    this.updateAssignToDisplayMode();
    this.assignmentStudents = this.formGroup.get(this.controlName);
    this.assignmentStudentValidationControl = this.assignmentStudents;

    if (!this.assignmentStudents) {
      console.warn(`Form control ${this.controlName} not found in form group.`);
    }

    this.assignmentStudents?.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateValidationMessages());

    this.formGroup.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateValidationMessages());

    this.runAfterFormDataLoaded(() => {
      this.syncFromForm(true);
      this.setupManageUserRouteStudent();
    });

    this.formApi.getFormDataChanges()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.syncFromForm(false));

    this.updateValidationMessages();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    this.sectionReadOnly = !!readOnly;
    this.updateAssignToDisplayMode();
  }

  protected submittedChanged(): void {
    this.updateValidationMessages();
  }

  resolvedStudentIdsChanged(studentIds: number[]): void {
    this.resolvedStudentIds = this.getEffectiveStudentIds(studentIds);
    this.updateStudentTotalLabel(this.resolvedStudentIds.length);

    if (this.syncingFromForm) {
      return;
    }

    this.updateAssignmentStudents(this.resolvedStudentIds);
  }

  trackDisplayItem(index: number, item: string): string {
    return item;
  }

  private syncFromForm(hydrateSelection: boolean): void {
    const assignment = this.formApi.getFormData<AssignmentEntity>();
    const aviatorUser = this.adminManagerService.getAviatorUser();
    const nextFlightSchoolId = this.resolveNumberValue(assignment, 'FlightSchoolId', 'SchoolId') || aviatorUser?.FlightSchoolId;
    const nextCourseId = this.resolveNumberValue(assignment, 'CourseId');

    if (this.flightSchoolId !== nextFlightSchoolId) {
      this.flightSchoolId = nextFlightSchoolId;
    }

    if (this.courseId !== nextCourseId) {
      this.courseId = nextCourseId;
    }

    const activeStudents = this.getActiveAssignmentStudents();
    const studentIds = activeStudents
      .map(student => student.StudentId)
      .filter((studentId): studentId is number => !!studentId);

    if (!this.routeLockedStudentId) {
      this.displayItems = activeStudents.map(student => student.StudentIdDisplay || `Student ${student.StudentId}`);
    }

    if (!hydrateSelection || this.selectionHydratedFromForm) {
      return;
    }

    this.syncingFromForm = true;
    this.selectionValue = {
      studentIds: this.distinct(studentIds),
      flightSchoolGroupIds: []
    };
    this.resolvedStudentIds = [...this.selectionValue.studentIds];
    this.updateStudentTotalLabel(this.resolvedStudentIds.length);
    this.selectionHydratedFromForm = true;
    this.syncingFromForm = false;
  }

  ignoredStudentCountChanged(ignoredStudentCount: number): void {
    const validStudentCount = this.resolvedStudentIds.length;
    this.studentTotalLabel = ignoredStudentCount > 0
      ? `${validStudentCount} students • ${ignoredStudentCount} ignored`
      : `${validStudentCount} ${validStudentCount === 1 ? 'student' : 'students'} total`;
    this.studentTotalTitle = ignoredStudentCount > 0
      ? `${validStudentCount} students will be assigned. ${ignoredStudentCount} will be ignored because they do not have the selected course.`
      : `${validStudentCount} ${validStudentCount === 1 ? 'student will' : 'students will'} be assigned.`;
  }

  private setupManageUserRouteStudent(): void {
    const userId = this.getManageUserAssignmentAddUserId();

    if (!userId || this.routeLockedStudentId || this.routeLockInProgress) {
      return;
    }

    this.routeLockApplies = true;
    this.routeLockInProgress = true;
    this.updateAssignToDisplayMode();

    this.studentRepositoryService.getStudentFromUserId(userId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(student => {
        this.routeLockInProgress = false;

        if (!student?.StudentId) {
          this.routeLockApplies = false;
          this.updateAssignToDisplayMode();
          return;
        }

        this.routeLockedStudentId = Number(student.StudentId);
        this.applyRouteLockedStudent(student);
        this.updateAssignToDisplayMode();
      }, () => {
        this.routeLockInProgress = false;
        this.routeLockApplies = false;
        this.updateAssignToDisplayMode();
      });
  }

  private applyRouteLockedStudent(student: StudentEntity): void {
    const studentId = Number(student.StudentId);
    const studentDisplay = this.resolveStudentDisplay(student, studentId);

    this.routeLockedStudentDisplay = studentDisplay;
    this.studentLabelOverrides = {
      [studentId]: studentDisplay
    };
    this.displayItems = [studentDisplay];
    this.updateStudentTotalLabel(1);
    this.syncingFromForm = true;
    this.selectionValue = {
      studentIds: [studentId],
      flightSchoolGroupIds: []
    };
    this.resolvedStudentIds = [studentId];
    this.selectionHydratedFromForm = true;
    this.syncingFromForm = false;

    this.formApi.patchVariableContext({ StudentId: studentId });
    this.updateAssignmentStudents([studentId], studentDisplay);
  }

  private updateAssignmentStudents(studentIds: number[], studentDisplay?: string): void {
    if (!this.assignmentStudents) {
      console.warn(`Form control ${this.controlName} not found in form group. Student group selection is loaded but cannot update the form value.`);
      return;
    }

    const selectedStudentIds = this.distinct(studentIds || []);
    const selectedStudentIdSet = new Set(selectedStudentIds);
    const assignment = this.formApi.getFormData<AssignmentEntity>();
    const currentAssignmentStudents = (assignment?.AssignmentStudent || this.assignmentStudents.value || []) as AssignmentStudentEntity[];
    const allAssignmentStudents = currentAssignmentStudents
      .filter(assignmentStudent => selectedStudentIdSet.has(assignmentStudent.StudentId)
        || Number(assignmentStudent.AssignmentStudentId || 0) > 0);

    allAssignmentStudents.forEach(assignmentStudent => {
      assignmentStudent._isDeleted = !selectedStudentIdSet.has(assignmentStudent.StudentId);
    });

    for (const studentId of selectedStudentIds) {
      let assignmentStudent = allAssignmentStudents.find(item => item.StudentId === studentId);

      if (!assignmentStudent) {
        assignmentStudent = AppMetaData.AssignmentStudent.CreateEntity();
        assignmentStudent.StudentId = studentId;
        allAssignmentStudents.push(assignmentStudent);
      }

      if (studentDisplay && studentId === this.routeLockedStudentId && !assignmentStudent.StudentIdDisplay) {
        assignmentStudent.StudentIdDisplay = studentDisplay;
      }

      assignmentStudent._isDeleted = false;
    }

    const patchData: Partial<AssignmentEntity> = deepCopy({ AssignmentStudent: allAssignmentStudents });

    
    this.formApi.patchFormData(patchData);
    this.formApi.patchContextData('data', patchData);
    this.displayItems = this.routeLockedStudentId
      ? [this.routeLockedStudentDisplay || `Student ${this.routeLockedStudentId}`]
      : allAssignmentStudents
        .filter(student => student._isDeleted !== true)
        .map(student => student.StudentIdDisplay || `Student ${student.StudentId}`);
    this.updateStudentTotalLabel(selectedStudentIds.length);
    this.updateValidationMessages();
  }

  private getManageUserAssignmentAddUserId(): number {
    const path = (this.router.url || '').split('?')[0].split('#')[0];
    const match = /^\/manage-users\/edit\/(\d+)\/assignments\/add\/?$/.exec(path);

    return match ? Number(match[1]) : null;
  }

  private updateAssignToDisplayMode(): void {
    this.assignToLocked = this.routeLockApplies || !!this.routeLockedStudentId;
    this.showStudentGroupSelectControl = !this.sectionReadOnly || this.assignToLocked;
  }

  private resolveStudentDisplay(student: StudentEntity, studentId: number): string {
    const firstName = (student as any).DwUserFirstName || student.DwUser?.FirstName || '';
    const lastName = (student as any).DwUserLastName || student.DwUser?.LastName || '';

    return `${firstName} ${lastName}`.trim() || student.UserIdDisplay || `Student ${studentId}`;
  }

  private getActiveAssignmentStudents(): AssignmentStudentEntity[] {
    return (((this.assignmentStudents?.value || []) as AssignmentStudentEntity[])
      .filter(student => student._isDeleted !== true));
  }

  private distinct(values: number[]): number[] {
    return values.filter((value, index, source) => source.indexOf(value) === index);
  }

  private getEffectiveStudentIds(studentIds: number[]): number[] {
    return this.routeLockedStudentId
      ? [this.routeLockedStudentId]
      : this.distinct(studentIds || []);
  }

  private updateStudentTotalLabel(count: number): void {
    this.studentTotalLabel = `${count} ${count === 1 ? 'student' : 'students'} total`;
    this.studentTotalTitle = `${count} ${count === 1 ? 'student will' : 'students will'} be assigned.`;
  }

  private updateValidationMessages(): void {
    const assignmentStudentHasError = this.hasStudentCountError(this.assignmentStudents);

    this.showAssignmentStudentValidationMessage = !!this.submitted && assignmentStudentHasError;
    this.showRootStudentValidationMessage = !!this.submitted && !assignmentStudentHasError && this.hasStudentCountError(this.formGroup);
  }

  private hasStudentCountError(control?: AbstractControl): boolean {
    const errors = control?.errors;

    if (!errors) {
      return false;
    }

    return Object.keys(errors).some(errorKey => errorKey === 'studentCountError' || errorKey.indexOf('invalidCount') === 0);
  }

  private resolveNumberValue(formData: any, ...fieldNames: string[]): number {
    for (const fieldName of fieldNames) {
      const value = formData?.[fieldName] ?? this.formGroup?.get(fieldName)?.value;

      if (value !== null && value !== undefined && value !== '') {
        return Number(value);
      }
    }

    return null;
  }
}
